import React from "react";
import { useLocation } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Button,
  Text,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from "@fluentui/react-components";
import { Spinner } from "@fluentui/react-components";
import { Filter24Regular, ArrowSync24Regular } from "@fluentui/react-icons";
import {
  BreadcrumbBar,
  DimensionSearch,
  DimensionSelector,
  AnalyticsSettingsDrawer,
  DataGrid,
} from "../components";
import {
  useAuth,
  useCountries,
  useFilters,
  useInvoiceData,
  useIsMobile,
  usePermissions,
  useSalesPeople,
} from "../hooks";
import { DIMENSION_LABELS } from "../types";
import type { AnalyticsSettings, UserRole } from "../types";

const useStyles = makeStyles({
  page: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: 0,
  },
  grid: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: 0,
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalXL,
  },
  topBar: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    "@media (min-width: 640px)": {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: tokens.spacingVerticalXS,
    },
  },
  breadcrumbBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  breadcrumb: {
    flex: "1 1 0",
    minWidth: 0,
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    flexShrink: 0,
    gap: tokens.spacingHorizontalS,
  },
  rowCount: {
    marginLeft: "auto",
    maxWidth: "100%",
    textAlign: "right",
    color: tokens.colorNeutralForeground3,
  },
  errorBar: {
    marginBottom: tokens.spacingVerticalL,
  },
});

export const SalesAnalyticsPage: React.FC = () => {
  const styles = useStyles();
  const { isAuthenticated, userRole } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { accessibleMetrics, filterAccessibleMetrics } =
    usePermissions(userRole);
  const [drawerSession, setDrawerSession] = React.useState<{
    settings: AnalyticsSettings;
    locationKey: string;
    role: UserRole;
  } | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");

  const {
    selectedMetrics,
    applyAnalyticsSettings,
    currentDimension,
    setCurrentDimension,
    filters,
    addFilter,
    navigateToFilter,
    clearFilters,
    liquidationFilter,
    exceptionFilter,
    timePeriod,
    selectedCountries,
    selectedSalesPeople,
    sortConfig,
    handleSort,
  } = useFilters();

  React.useEffect(() => {
    setDrawerSession(null);
  }, [location.key, userRole]);

  // Reset search when dimension changes
  React.useEffect(() => {
    setSearchQuery("");
  }, [currentDimension]);

  const { countries, isLoading: countriesLoading } =
    useCountries(isAuthenticated);

  const { salesPeople, isLoading: salesPeopleLoading } =
    useSalesPeople(isAuthenticated);

  const permittedMetrics = React.useMemo(
    () => filterAccessibleMetrics(selectedMetrics),
    [filterAccessibleMetrics, selectedMetrics],
  );

  const appliedSettings: AnalyticsSettings = {
    selectedMetrics: permittedMetrics,
    liquidationFilter,
    exceptionFilter,
    timePeriod,
    selectedCountries,
    selectedSalesPeople,
  };
  const settingsOpen =
    drawerSession !== null &&
    drawerSession.locationKey === location.key &&
    drawerSession.role === userRole;

  const handleOpenSettings = () => {
    setDrawerSession({
      settings: {
        ...appliedSettings,
        selectedMetrics: [...permittedMetrics],
        selectedCountries: [...selectedCountries],
        selectedSalesPeople: [...selectedSalesPeople],
      },
      locationKey: location.key,
      role: userRole,
    });
  };

  const handleSettingsChange = React.useCallback(
    (settings: AnalyticsSettings) => {
      setDrawerSession((current) =>
        current ? { ...current, settings } : null,
      );
    },
    [],
  );

  const handleDismissSettings = React.useCallback(
    () => setDrawerSession(null),
    [],
  );

  const handleApplySettings = React.useCallback(
    (settings: AnalyticsSettings) => {
      applyAnalyticsSettings({
        ...settings,
        selectedMetrics: filterAccessibleMetrics(settings.selectedMetrics),
      });
      setDrawerSession(null);
    },
    [applyAnalyticsSettings, filterAccessibleMetrics],
  );

  const {
    data,
    isLoading: dataLoading,
    isFetching,
    error,
    refetch,
  } = useInvoiceData({
    dimension: currentDimension,
    filters,
    liquidationFilter,
    exceptionFilter,
    timePeriod,
    selectedMetrics: permittedMetrics,
    userRole,
    selectedCountries,
    selectedSalesPeople,
    sortConfig,
    isAuthenticated,
  });

  const deferredSearchQuery = React.useDeferredValue(searchQuery);
  const isSearchPending = searchQuery !== deferredSearchQuery;
  const searchIndex = React.useMemo(
    () =>
      data.map((row) => ({
        row,
        label: row.dimensionLabel.toLowerCase(),
        value: row.dimensionValue.toLowerCase(),
      })),
    [data],
  );
  const filteredData = React.useMemo(() => {
    if (!deferredSearchQuery.trim()) return data;
    const query = deferredSearchQuery.toLowerCase();
    return searchIndex
      .filter(
        ({ label, value }) => label.includes(query) || value.includes(query),
      )
      .map(({ row }) => row);
  }, [data, searchIndex, deferredSearchQuery]);

  const handleRowClick = React.useCallback(
    (row: { dimensionValue: string; dimensionLabel: string }) => {
      addFilter({
        dimension: currentDimension,
        value: row.dimensionValue,
        label: row.dimensionLabel,
      });
    },
    [addFilter, currentDimension],
  );

  return (
    <div className={styles.page}>
      <div className={styles.controls}>
        {/* Top Bar: Search and actions */}
        <div className={styles.topBar}>
          {isMobile && (
            <DimensionSelector
              value={currentDimension}
              onChange={setCurrentDimension}
            />
          )}

          <DimensionSearch
            dimensionLabel={DIMENSION_LABELS[currentDimension]}
            value={searchQuery}
            onChange={setSearchQuery}
          />

          <div className={styles.actionButtons}>
            <Button
              appearance="secondary"
              icon={
                isFetching && !dataLoading ? (
                  <Spinner size="tiny" />
                ) : (
                  <ArrowSync24Regular />
                )
              }
              onClick={() => refetch()}
              disabled={isFetching && !dataLoading}
            >
              Refresh
            </Button>

            <Button
              appearance={settingsOpen ? "primary" : "secondary"}
              icon={<Filter24Regular />}
              onClick={handleOpenSettings}
              aria-haspopup="dialog"
              aria-expanded={settingsOpen}
            >
              Options
            </Button>
          </div>
        </div>
      </div>

      <AnalyticsSettingsDrawer
        open={settingsOpen}
        settings={drawerSession?.settings ?? appliedSettings}
        appliedSettings={appliedSettings}
        onChange={handleSettingsChange}
        onApply={handleApplySettings}
        onDismiss={handleDismissSettings}
        accessibleMetrics={accessibleMetrics}
        countries={countries}
        countriesLoading={countriesLoading}
        salesPeople={salesPeople}
        salesPeopleLoading={salesPeopleLoading}
      />

      {/* Error State */}
      {error && (
        <MessageBar intent="error" className={styles.errorBar}>
          <MessageBarBody>
            <MessageBarTitle>Error loading data</MessageBarTitle>
            {(error as Error).message ||
              "An unexpected error occurred. Please try again."}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Breadcrumb Bar */}
      {!isMobile && (
        <div className={styles.breadcrumbBar}>
          <div className={styles.breadcrumb}>
            <BreadcrumbBar
              items={filters}
              currentDimension={currentDimension}
              onDimensionChange={setCurrentDimension}
              onNavigate={navigateToFilter}
              onClearAll={clearFilters}
            />
          </div>
          {filteredData.length > 0 && (
            <Text size={200} className={styles.rowCount}>
              Showing {filteredData.length}
              {filteredData.length !== data.length && ` of ${data.length}`}
              {filteredData.length === 1 ? " row" : " rows"}
              {filters.length > 0 &&
                ` (filtered by ${filters.length} ${filters.length === 1 ? "dimension" : "dimensions"})`}
            </Text>
          )}
        </div>
      )}

      {/* Data Grid */}
      <div className={styles.grid} aria-busy={isSearchPending}>
        <DataGrid
          data={filteredData}
          selectedMetrics={permittedMetrics}
          currentDimension={currentDimension}
          sortConfig={sortConfig}
          onSort={handleSort}
          onRowClick={handleRowClick}
          isLoading={dataLoading}
        />
      </div>
    </div>
  );
};
